/**
 * Navigator Tuning · Review changes (feature 130). Draft × live parameter diff and the
 * draft's impact on the newest complete plans (Tuned × each plan's own config, from the
 * backend). No score deltas across configs: scores are not comparable between them.
 * "See the affected plans" opens Plan history in affected mode. Layout: design/Diff.dc.html
 * (AI generation group and the single-plan breakdown removed).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDraftConfigVersion, useImpact, useLiveConfigVersion } from '../hooks/useConfigVersions';
import type { ImpactSample } from '../types/navigator-tuning.type';
import { PlanHistoryScreen, type AffectedPlans } from './PlanHistoryScreen';
import { changesLabel, paramChanges } from './parameters';

const SAMPLES: { value: ImpactSample; label: string }[] = [
  { value: 'last10', label: 'Last 10 plans' },
  { value: 'last50', label: 'Last 50 plans' },
  { value: 'last14days', label: 'Last 14 days' },
];

const percent = (share: number | null) => (share === null ? '—' : `${Math.round(share * 100)}%`);

const card = 'rounded-[14px] border border-border-subtle bg-white';

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div
      role="group"
      aria-label={label}
      className="flex flex-col gap-1 rounded-[10px] border border-[#efeae1] bg-[#fcfbf8] p-3.5"
    >
      <span className="text-[13px] font-normal text-[#56607a]">{label}</span>
      <span className="text-2xl font-bold text-[#1b2232]">{value}</span>
    </div>
  );
}

export function ReviewChangesScreen() {
  const live = useLiveConfigVersion();
  const draft = useDraftConfigVersion();
  const [sample, setSample] = useState<ImpactSample>('last10');
  const [showAffected, setShowAffected] = useState(false);
  const impact = useImpact(draft.data?.params ?? null, sample);

  if (live.isLoading || draft.isLoading) return <p className="text-[#56607a]">Loading…</p>;
  if (live.isError || draft.isError || !live.data)
    return <p role="alert">Failed to load the config versions.</p>;
  const liveTag = `v${live.data.versionNumber}`;
  if (!draft.data)
    return (
      <p className={`${card} p-5 text-[#56607a]`}>
        No draft changes to review. Open a plan in Plan history and move a parameter to start a
        draft.
      </p>
    );

  const changes = paramChanges(live.data.params, draft.data.params);
  const result = impact.data;
  const affected: AffectedPlans = Object.fromEntries(
    (result?.affected ?? []).map(({ sessionId, ...flags }) => [sessionId, flags])
  );

  if (showAffected)
    return (
      <div className="space-y-4 bg-background">
        <button
          type="button"
          onClick={() => setShowAffected(false)}
          className="min-h-10 rounded-lg border border-[#d9d3c7] bg-white px-3.5 text-sm font-semibold text-[#1b2232] hover:bg-[#f4f0e9]"
        >
          Back to review changes
        </button>
        <PlanHistoryScreen affected={affected} />
      </div>
    );

  return (
    <div className="space-y-5 bg-background">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-recoleta text-[30px] font-semibold text-[#1b2232]">
            {changesLabel(changes.length)} vs. live config {liveTag}
          </h2>
          <p className="mt-1.5 text-[#56607a]">
            What you changed in the draft and what it would do to recent plans.
          </p>
        </div>
        <Link
          to="/admin/navigator-tuning/config-versions"
          className="inline-flex min-h-11 items-center rounded-lg bg-bquest-green px-4 text-sm font-bold text-white hover:bg-[#014d41]"
        >
          {live.data.versionNumber === null
            ? 'Publish…'
            : `Publish as v${live.data.versionNumber + 1}…`}
        </Link>
      </header>

      <section className={`${card} overflow-hidden p-0`}>
        <h3
          id="param-changes-heading"
          className="border-b border-[#efeae1] px-5 py-4 text-base font-bold"
        >
          Parameter changes
        </h3>
        {changes.length ? (
          <div className="overflow-x-auto">
            <table
              aria-labelledby="param-changes-heading"
              className="w-full min-w-[520px] border-collapse text-left text-sm"
            >
              <thead>
                <tr className="bg-[#f4f0e9] text-xs font-semibold uppercase tracking-[0.04em] text-[#56607a]">
                  <th className="px-5 py-2.5 font-semibold">Parameter</th>
                  <th className="px-3 py-2.5 font-semibold">Live ({liveTag})</th>
                  <th className="px-5 py-2.5 font-semibold">Draft</th>
                </tr>
              </thead>
              <tbody>
                {changes.map((c) => (
                  <tr key={c.key} className="border-t border-[#efeae1]">
                    <td className="px-5 py-3.5">
                      <span className="font-bold text-[#1b2232]">{c.label}</span>
                      <span className="block text-xs text-[#56607a]">{c.group}</span>
                    </td>
                    <td className="px-3 py-3.5 text-[#56607a] line-through">{c.live}</td>
                    <td className="px-5 py-3.5 font-bold text-[#1b2232]">{c.draft}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="px-5 py-4 text-sm text-[#56607a]">
            The draft has the same parameters as the live config.
          </p>
        )}
      </section>

      <section
        aria-labelledby="impact-heading"
        className={`${card} flex flex-col gap-3.5 px-5 py-[18px]`}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-col gap-1">
            <h3 id="impact-heading" className="text-base font-bold text-[#1b2232]">
              Impact on recent plans
            </h3>
            <p className="text-sm text-[#56607a]">
              Draft parameters replayed on the stored candidates of complete plans, compared with
              the config each plan was generated with. No AI calls.
            </p>
          </div>
          <div
            role="group"
            aria-label="Sample"
            className="flex gap-0.5 rounded-lg bg-[#f1ede6] p-[3px]"
          >
            {SAMPLES.map((s) => (
              <button
                key={s.value}
                type="button"
                aria-pressed={s.value === sample}
                onClick={() => setSample(s.value)}
                className={`min-h-[34px] rounded-md px-3 text-[13px] font-semibold ${s.value === sample ? 'bg-white text-[#014d41] shadow-sm' : 'text-[#56607a]'}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
        {impact.isFetching && (
          <p role="status" className="text-sm text-[#56607a]">
            Replaying plans…
          </p>
        )}
        {impact.isError && <p role="alert">Could not compute the impact. Try again.</p>}
        {result && (
          <>
            {result.capped && (
              <p className="text-sm text-[#56607a]">
                More than 50 plans: showing the 50 most recent.
              </p>
            )}
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Metric
                label="Best Match changed"
                value={`${result.bestMatchChanged} of ${result.plansCount}`}
              />
              <Metric
                label="Top Match changed (any specialty)"
                value={`${result.topMatchChanged} of ${result.plansCount}`}
              />
              <Metric
                label="Empty specialties"
                value={`${result.emptySpecialties.before} → ${result.emptySpecialties.after}`}
              />
              <Metric
                label="Share of Best Match that is bQuest preferred"
                value={`${percent(result.bquestPreferredShare.before)} → ${percent(result.bquestPreferredShare.after)}`}
              />
            </div>
            <p className="text-xs text-[#56607a]">
              bQuest preferred uses each provider’s current bQuest preferred flag, not the one at
              the time of the plan.
            </p>
            <button
              type="button"
              disabled={result.affected.length === 0}
              onClick={() => setShowAffected(true)}
              className="self-start text-sm font-semibold text-bquest-green hover:text-[#014d41] disabled:cursor-not-allowed disabled:opacity-50"
            >
              See the affected plans
            </button>
          </>
        )}
      </section>
    </div>
  );
}
